import React, { useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const DEFAULT_PRICING = [
  { id: 'low', name: 'Low Season', start_month: 10, start_day: 1, end_month: 5, end_day: 31, rate: 400, min_stay: 3 },
  { id: 'mid', name: 'Mid Season', start_month: 6, start_day: 1, end_month: 6, end_day: 30, rate: 630, min_stay: 5 },
  { id: 'high', name: 'High Season', start_month: 7, start_day: 1, end_month: 7, end_day: 31, rate: 900, min_stay: 7 },
  { id: 'august', name: 'August Peak', start_month: 8, start_day: 1, end_month: 8, end_day: 31, rate: 1300, min_stay: 7 },
  { id: 'shoulder', name: 'Shoulder', start_month: 9, start_day: 1, end_month: 9, end_day: 30, rate: 540, min_stay: 5 },
];

function getPricing() {
  try {
    const stored = JSON.parse(localStorage.getItem('pricingSeasons'));
    return stored && stored.length > 0 ? stored : DEFAULT_PRICING;
  } catch { return DEFAULT_PRICING; }
}

export default function PricingTab() {
  const [seasons, setSeasons] = useState(getPricing());
  const [saved, setSaved] = useState(false);

  const updateSeason = (index, field, value) => {
    const updated = [...seasons];
    if (['rate', 'min_stay', 'start_month', 'start_day', 'end_month', 'end_day'].includes(field)) {
      updated[index][field] = Number(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setSeasons(updated);
    setSaved(false);
  };

  const addSeason = () => {
    setSeasons([...seasons, {
      id: `custom-${Date.now()}`,
      name: 'New Season',
      start_month: 1, start_day: 1,
      end_month: 1, end_day: 31,
      rate: 400, min_stay: 3,
    }]);
    setSaved(false);
  };

  const removeSeason = (index) => {
    setSeasons(seasons.filter((_, i) => i !== index));
    setSaved(false);
  };

  const savePricing = () => {
    localStorage.setItem('pricingSeasons', JSON.stringify(seasons));
    window.dispatchEvent(new Event('storage'));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const formatDateRange = (s) =>
    `${MONTHS[s.start_month - 1]} ${s.start_day} – ${MONTHS[s.end_month - 1]} ${s.end_day}`;

  return (
    <div className="admin-pricing" data-testid="pricing-tab">
      <div className="admin-pricing-header">
        <h2>Pricing Manager</h2>
        <div className="admin-pricing-actions">
          <button className="admin-action-btn secondary" onClick={addSeason} data-testid="add-season-btn">
            <Plus className="h-4 w-4" /> Add Season
          </button>
          <button className="admin-action-btn" onClick={savePricing} data-testid="save-pricing-btn">
            <Save className="h-4 w-4" />
            {saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="admin-pricing-table-wrap">
        <table className="admin-pricing-table" data-testid="pricing-table">
          <thead>
            <tr>
              <th>Season Name</th>
              <th>Start</th>
              <th>End</th>
              <th>€/Night</th>
              <th>Min Stay</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {seasons.map((s, i) => (
              <tr key={s.id || i} data-testid={`pricing-row-${i}`}>
                <td>
                  <input
                    type="text"
                    value={s.name}
                    onChange={(e) => updateSeason(i, 'name', e.target.value)}
                    className="admin-input"
                  />
                </td>
                <td className="date-cell">
                  <select value={s.start_month} onChange={(e) => updateSeason(i, 'start_month', e.target.value)} className="admin-select">
                    {MONTHS.map((m, mi) => <option key={mi} value={mi + 1}>{m}</option>)}
                  </select>
                  <input type="number" min="1" max="31" value={s.start_day} onChange={(e) => updateSeason(i, 'start_day', e.target.value)} className="admin-input day-input" />
                </td>
                <td className="date-cell">
                  <select value={s.end_month} onChange={(e) => updateSeason(i, 'end_month', e.target.value)} className="admin-select">
                    {MONTHS.map((m, mi) => <option key={mi} value={mi + 1}>{m}</option>)}
                  </select>
                  <input type="number" min="1" max="31" value={s.end_day} onChange={(e) => updateSeason(i, 'end_day', e.target.value)} className="admin-input day-input" />
                </td>
                <td>
                  <div className="price-input-wrap">
                    <span>€</span>
                    <input type="number" min="0" value={s.rate} onChange={(e) => updateSeason(i, 'rate', e.target.value)} className="admin-input price-input" />
                  </div>
                </td>
                <td>
                  <div className="stay-input-wrap">
                    <input type="number" min="1" max="30" value={s.min_stay} onChange={(e) => updateSeason(i, 'min_stay', e.target.value)} className="admin-input stay-input" />
                    <span>nights</span>
                  </div>
                </td>
                <td>
                  <button className="admin-delete-btn" onClick={() => removeSeason(i)} data-testid={`delete-season-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-pricing-preview">
        <h3>Preview</h3>
        <div className="pricing-preview-cards">
          {seasons.map((s, i) => (
            <div key={i} className="pricing-preview-card">
              <div className="ppc-name">{s.name}</div>
              <div className="ppc-dates">{formatDateRange(s)}</div>
              <div className="ppc-rate">€{s.rate}<span>/night</span></div>
              <div className="ppc-stay">Min {s.min_stay} nights</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
